import request from '../../../api/request';

export enum NonStructuredType {
  COURSE = 'course',
  PROGRAM_COURSE = 'program_course',
}

export interface FormDataResponseType {
  schools: Array<{ id: number; name: string }>; //Plantel - Facultad
  campuses: Array<{ id: number; name: string }>; //sede
  modalities: Array<{ id: number; name: string }>;
  academicLevels: Array<{ id: number; name: string }>;
}

export interface Course {
  id: number;
  name: string;
  code: string;
  schoolId: number;
  modalityId: number;
  totalHours: number | null;
  modality?: { id: number; name: string };
  school?: { id: number; name: string };
  academicLevel?: { id: number; name: string };
}

export interface ActivityType {
  id: string;
  name: string;
}

export interface CourseFormDataResponseType {
  courses: Course[];
  activityTypes: ActivityType[];
  academicLevels: Array<{ id: number; name: string }>;
}

export interface ActivityHour {
  activityTypeId: string;
  hours: number;
}

export interface CreateCourseRequest {
  courseId: number;
  name: string;
  code: string;
  description: string;
  hasCertificate: boolean;
  activityHours: ActivityHour[];
  academicLevelId: number;
}

export interface ProgramUnitSubject {
  id: number;
  name?: string;
  code?: string;
  credits?: number;
  modality?: { id: number; name: string; deliveryMode: string };
  academicLevel?: { id: number; name: string };
}

export interface ProgramUnit {
  id?: number;
  name: string;
  subjects: ProgramUnitSubject[];
  message?: string;
}

export interface CreateProgramRequest {
  name: string;
  code: string;
  modalityId: number;
  schoolId: number;
  description?: string;
  hasCertificate: boolean;
  activityHours: ActivityHour[];
  units: ProgramUnit[];
  imageId?: number;
  academicLevelId: number;
}

export interface ProgramFormDataResponseType {
  schools: Array<{ id: number; name: string }>;
  modalities: Array<{ id: number; name: string; deliveryMode: string }>;
  activityTypes: ActivityType[];
  subjects: ProgramUnitSubject[];
  academicLevels: Array<{ id: number; name: string }>;
}

export type StudyPlanType = 'course' | 'program_course';

export type StudyPlanVersionStatus =
  | 'Borrador'
  | 'Activo'
  | 'Vigente'
  | 'Cerrado';

export interface NonStructuredStudyPlanInfo {
  id: number;
  type: StudyPlanType;
  hoursTotal: number | null;
  description: string | null;
  hasCertificate: boolean;
  activityTypeHours: ActivityTypeHours[];
}

export interface ActivityTypeHours {
  id: number;
  activityTypeId: number;
  activityType: {
    id: number;
    name: string;
  };
  hours: number;
}

export interface SemesterCourse {
  id: number;
  course: ProgramUnitSubject;
}

export interface Semester {
  id: number;
  name: string;
  order: number;
  semesterCourses: SemesterCourse[];
}

export interface CurriculumImage {
  id: number;
  name: string;
  extension: string;
  url: string;
}

export interface Curriculum {
  id: number;
  semesters: Semester[];
  image?: CurriculumImage | null;
}

export interface StudyPlanVersion {
  id: number;
  status: StudyPlanVersionStatus;
  curriculums?: Curriculum[];
}

export interface StudyPlanListItem {
  id: number;
  name: string;
  code: string;
  modality: { id: number; name: string } | null;
  school: { id: number; name: string } | null;
  academicLevel?: { id: number; name: string } | null;
  academicLevelId?: number;
  createdAt: string;
  studyPlanCe?: NonStructuredStudyPlanInfo;
  versions?: StudyPlanVersion[];
  imageId?: number;
  isInActiveOffer?: boolean;
}

export interface StudyPlanListFilters {
  name?: string;
  code?: string;
  schoolId?: number;
  modalityId?: number;
  campusId?: number;
  academicLevelId?: number;
}

export interface StudyPlanListResponse {
  data: StudyPlanListItem[];
  total: number;
  total_pages: number;
}

export const CONTINUOUS_EDUCATION = {
  GET_FORM_DATA: () =>
    '/maintainers-portal/non-structured/study-plans/form-data',
  GET_LIST: () => '/maintainers-portal/non-structured/study-plans',
  GET_BY_ID: (id: number) =>
    `/maintainers-portal/non-structured/study-plans/${id}`,
  GET_COURSE_FORM_DATA: () =>
    '/maintainers-portal/non-structured/study-plans/courses/form-data',
  CREATE_COURSE: () => '/maintainers-portal/non-structured/study-plans/courses',
  UPDATE_COURSE: (id: number) =>
    `/maintainers-portal/non-structured/study-plans/courses/${id}`,
  GET_PROGRAM_FORM_DATA: () =>
    '/maintainers-portal/non-structured/study-plans/programs/form-data',
  CREATE_PROGRAM: () =>
    '/maintainers-portal/non-structured/study-plans/programs',
  UPDATE_PROGRAM: (id: number) =>
    `/maintainers-portal/non-structured/study-plans/programs/${id}`,
  UPDATE_VERSION_STATUS: (versionId: number) =>
    `/maintainers-portal/non-structured/study-plans/versions/${versionId}/status`,
  DELETE_PROGRAM: (id: number) =>
    `/maintainers-portal/non-structured/study-plans/programs/delete/${id}`,
  DELETE_COURSE: (id: number) =>
    `/maintainers-portal/non-structured/study-plans/courses/delete/${id}`,
};

/**
 * Obtiene los datos para llenar el filtro de planes de estudio de educación continua
 * @returns
 */
export const getFormDataRequest = () => {
  return request<FormDataResponseType>(CONTINUOUS_EDUCATION.GET_FORM_DATA());
};

/**
 * Obtiene los datos para crear un curso de educación continua
 * (asignaturas disponibles y tipos de actividad)
 * @returns
 */
export const getCourseFormDataRequest = () => {
  return request<CourseFormDataResponseType>(
    CONTINUOUS_EDUCATION.GET_COURSE_FORM_DATA(),
  );
};

/**
 * Crea un curso de educación continua
 * @param data
 * @returns
 */
export const createCourseRequest = (data: CreateCourseRequest) => {
  return request<{ id: number }>(CONTINUOUS_EDUCATION.CREATE_COURSE(), {
    method: 'POST',
    data,
  });
};

/**
 * Obtiene los datos para crear un programa de educación continua
 * (modalidades, facultades, tipos de actividad y asignaturas disponibles)
 * @returns
 */
export const getProgramFormDataRequest = () => {
  return request<ProgramFormDataResponseType>(
    CONTINUOUS_EDUCATION.GET_PROGRAM_FORM_DATA(),
  );
};

/**
 * Crea un programa de educación continua
 * @param data
 * @returns
 */
export const createProgramRequest = (data: CreateProgramRequest) => {
  return request<{ id: number }>(CONTINUOUS_EDUCATION.CREATE_PROGRAM(), {
    method: 'POST',
    data,
  });
};

/**
 * Obtiene el listado de planes de estudio de educación continua
 * @param filters - Filtros opcionales
 * @param page - Número de página (default: 0)
 * @param itemsPerPage - Cantidad de resultados por página (default: 10)
 * @returns
 */
export const getStudyPlansListRequest = (
  filters: StudyPlanListFilters = {},
  page: number = 0,
  itemsPerPage: number = 10,
) => {
  const params = new URLSearchParams();

  if (filters.name) {
    params.append('name', filters.name);
  }
  if (filters.code) {
    params.append('code', filters.code);
  }
  if (filters.schoolId) {
    params.append('schoolId', String(filters.schoolId));
  }
  if (filters.modalityId) {
    params.append('modalityId', String(filters.modalityId));
  }
  if (filters.academicLevelId) {
    params.append('academicLevelId', String(filters.academicLevelId));
  }
  if (filters.campusId) {
    params.append('campusId', String(filters.campusId));
  }

  params.append('page', String(page));
  params.append('items_per_page', String(itemsPerPage));

  const queryString = params.toString();
  const url = `${CONTINUOUS_EDUCATION.GET_LIST()}${
    queryString ? `?${queryString}` : ''
  }`;

  return request<StudyPlanListResponse>(url);
};

/**
 * Obtiene un plan de estudio por ID
 * @param id - ID del plan de estudio
 * @returns
 */
export const getStudyPlanByIdRequest = (id: number) => {
  return request<StudyPlanListItem>(CONTINUOUS_EDUCATION.GET_BY_ID(id));
};

/**
 * Actualiza un curso de educación continua
 * @param id - ID del plan de estudio
 * @param data - Datos del curso
 * @returns
 */
export const updateCourseRequest = (id: number, data: CreateCourseRequest) => {
  return request<{ id: number }>(CONTINUOUS_EDUCATION.UPDATE_COURSE(id), {
    method: 'PUT',
    data,
  });
};

/**
 * Actualiza un programa de educación continua
 * @param id - ID del plan de estudio
 * @param data - Datos del programa
 * @returns
 */
export const updateProgramRequest = (
  id: number,
  data: CreateProgramRequest,
) => {
  return request<{ id: number }>(CONTINUOUS_EDUCATION.UPDATE_PROGRAM(id), {
    method: 'PUT',
    data,
  });
};

/**
 * Actualiza el estado de una versión de plan de estudio
 * @param versionId - ID de la versión
 * @param newStatus - Nuevo estado
 * @returns
 */
export const updateVersionStatusRequest = (
  versionId: number,
  newStatus: StudyPlanVersionStatus,
) => {
  return request(CONTINUOUS_EDUCATION.UPDATE_VERSION_STATUS(versionId), {
    method: 'PUT',
    data: { newStatus },
  });
};

export const deleteStudyPlanRequest = (
  id: number,
  type?: NonStructuredType,
) => {
  const url =
    type === NonStructuredType.COURSE
      ? CONTINUOUS_EDUCATION.DELETE_COURSE(id)
      : CONTINUOUS_EDUCATION.DELETE_PROGRAM(id);
  return request(url, { method: 'PUT' });
};
